import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  KidsClubBooking,
  KidsClubCapacity,
  KidsClubCheckIn,
  EmergencyAlert,
} from '../../types';

interface KidsClubState {
  // Bookings
  myBookings: KidsClubBooking[];
  upcomingBookings: KidsClubBooking[];
  selectedBooking: KidsClubBooking | null;

  // Capacity
  availableCapacity: KidsClubCapacity[];

  // Check-ins (Staff)
  currentCheckIns: KidsClubCheckIn[];
  selectedCheckIn: KidsClubCheckIn | null;

  // Alerts (Staff)
  unresolvedAlerts: EmergencyAlert[];

  // Loading states
  loading: boolean;
  bookingLoading: boolean;
  checkInLoading: boolean;
  alertLoading: boolean;

  // Error states
  error: string | null;
  bookingError: string | null;
  checkInError: string | null;
  alertError: string | null;

  // Success states
  bookingSuccess: boolean;
  checkInSuccess: boolean;
  alertSuccess: boolean;
}

const initialState: KidsClubState = {
  myBookings: [],
  upcomingBookings: [],
  selectedBooking: null,
  availableCapacity: [],
  currentCheckIns: [],
  selectedCheckIn: null,
  unresolvedAlerts: [],
  loading: false,
  bookingLoading: false,
  checkInLoading: false,
  alertLoading: false,
  error: null,
  bookingError: null,
  checkInError: null,
  alertError: null,
  bookingSuccess: false,
  checkInSuccess: false,
  alertSuccess: false,
};

const kidsClubSlice = createSlice({
  name: 'kidsClub',
  initialState,
  reducers: {
    // ==========================================
    // Booking Actions
    // ==========================================
    fetchMyBookingsStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchMyBookingsSuccess: (state, action: PayloadAction<KidsClubBooking[]>) => {
      state.loading = false;
      state.myBookings = action.payload;
      state.error = null;
    },
    fetchMyBookingsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    fetchUpcomingBookingsStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchUpcomingBookingsSuccess: (state, action: PayloadAction<KidsClubBooking[]>) => {
      state.loading = false;
      state.upcomingBookings = action.payload;
      state.error = null;
    },
    fetchUpcomingBookingsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    createBookingStart: (state) => {
      state.bookingLoading = true;
      state.bookingError = null;
      state.bookingSuccess = false;
    },
    createBookingSuccess: (state, action: PayloadAction<KidsClubBooking>) => {
      state.bookingLoading = false;
      state.myBookings.push(action.payload);
      state.upcomingBookings.push(action.payload);
      state.bookingError = null;
      state.bookingSuccess = true;
    },
    createBookingFailure: (state, action: PayloadAction<string>) => {
      state.bookingLoading = false;
      state.bookingError = action.payload;
      state.bookingSuccess = false;
    },

    cancelBookingStart: (state) => {
      state.bookingLoading = true;
      state.bookingError = null;
    },
    cancelBookingSuccess: (state, action: PayloadAction<KidsClubBooking>) => {
      state.bookingLoading = false;
      // Update booking in lists
      state.myBookings = state.myBookings.map((b) =>
        b.id === action.payload.id ? action.payload : b
      );
      state.upcomingBookings = state.upcomingBookings.map((b) =>
        b.id === action.payload.id ? action.payload : b
      );
      state.bookingError = null;
    },
    cancelBookingFailure: (state, action: PayloadAction<string>) => {
      state.bookingLoading = false;
      state.bookingError = action.payload;
    },

    setSelectedBooking: (state, action: PayloadAction<KidsClubBooking | null>) => {
      state.selectedBooking = action.payload;
    },

    // ==========================================
    // Capacity Actions
    // ==========================================
    fetchCapacityStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchCapacitySuccess: (state, action: PayloadAction<KidsClubCapacity[]>) => {
      state.loading = false;
      state.availableCapacity = action.payload;
      state.error = null;
    },
    fetchCapacityFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // ==========================================
    // Check-In Actions (Staff)
    // ==========================================
    fetchCurrentCheckInsStart: (state) => {
      state.checkInLoading = true;
      state.checkInError = null;
    },
    fetchCurrentCheckInsSuccess: (state, action: PayloadAction<KidsClubCheckIn[]>) => {
      state.checkInLoading = false;
      state.currentCheckIns = action.payload;
      state.checkInError = null;
    },
    fetchCurrentCheckInsFailure: (state, action: PayloadAction<string>) => {
      state.checkInLoading = false;
      state.checkInError = action.payload;
    },

    performCheckInStart: (state) => {
      state.checkInLoading = true;
      state.checkInError = null;
      state.checkInSuccess = false;
    },
    performCheckInSuccess: (state, action: PayloadAction<KidsClubCheckIn>) => {
      state.checkInLoading = false;
      state.currentCheckIns.push(action.payload);
      state.checkInError = null;
      state.checkInSuccess = true;
    },
    performCheckInFailure: (state, action: PayloadAction<string>) => {
      state.checkInLoading = false;
      state.checkInError = action.payload;
      state.checkInSuccess = false;
    },

    performCheckOutStart: (state) => {
      state.checkInLoading = true;
      state.checkInError = null;
      state.checkInSuccess = false;
    },
    performCheckOutSuccess: (state, action: PayloadAction<KidsClubCheckIn>) => {
      state.checkInLoading = false;
      // Remove checked-out child from current check-ins list immediately
      state.currentCheckIns = state.currentCheckIns.filter((c) => c.id !== action.payload.id);
      state.checkInError = null;
      state.checkInSuccess = true;
    },
    performCheckOutFailure: (state, action: PayloadAction<string>) => {
      state.checkInLoading = false;
      state.checkInError = action.payload;
      state.checkInSuccess = false;
    },

    updateParentLocationStart: (state) => {
      state.checkInLoading = true;
      state.checkInError = null;
    },
    updateParentLocationSuccess: (state, action: PayloadAction<KidsClubCheckIn>) => {
      state.checkInLoading = false;
      state.currentCheckIns = state.currentCheckIns.map((c) =>
        c.id === action.payload.id ? action.payload : c
      );
      state.checkInError = null;
    },
    updateParentLocationFailure: (state, action: PayloadAction<string>) => {
      state.checkInLoading = false;
      state.checkInError = action.payload;
    },

    setSelectedCheckIn: (state, action: PayloadAction<KidsClubCheckIn | null>) => {
      state.selectedCheckIn = action.payload;
    },

    // ==========================================
    // Alert Actions (Staff)
    // ==========================================
    fetchUnresolvedAlertsStart: (state) => {
      state.alertLoading = true;
      state.alertError = null;
    },
    fetchUnresolvedAlertsSuccess: (state, action: PayloadAction<EmergencyAlert[]>) => {
      state.alertLoading = false;
      state.unresolvedAlerts = action.payload;
      state.alertError = null;
    },
    fetchUnresolvedAlertsFailure: (state, action: PayloadAction<string>) => {
      state.alertLoading = false;
      state.alertError = action.payload;
    },

    createAlertStart: (state) => {
      state.alertLoading = true;
      state.alertError = null;
      state.alertSuccess = false;
    },
    createAlertSuccess: (state, action: PayloadAction<EmergencyAlert>) => {
      state.alertLoading = false;
      state.unresolvedAlerts.push(action.payload);
      state.alertError = null;
      state.alertSuccess = true;
    },
    createAlertFailure: (state, action: PayloadAction<string>) => {
      state.alertLoading = false;
      state.alertError = action.payload;
      state.alertSuccess = false;
    },

    acknowledgeAlertStart: (state) => {
      state.alertLoading = true;
      state.alertError = null;
    },
    acknowledgeAlertSuccess: (state, action: PayloadAction<EmergencyAlert>) => {
      state.alertLoading = false;
      state.unresolvedAlerts = state.unresolvedAlerts.map((a) =>
        a.id === action.payload.id ? action.payload : a
      );
      state.alertError = null;
    },
    acknowledgeAlertFailure: (state, action: PayloadAction<string>) => {
      state.alertLoading = false;
      state.alertError = action.payload;
    },

    resolveAlertStart: (state) => {
      state.alertLoading = true;
      state.alertError = null;
    },
    resolveAlertSuccess: (state, action: PayloadAction<string>) => {
      state.alertLoading = false;
      // Remove resolved alert from list
      state.unresolvedAlerts = state.unresolvedAlerts.filter((a) => a.id !== action.payload);
      state.alertError = null;
    },
    resolveAlertFailure: (state, action: PayloadAction<string>) => {
      state.alertLoading = false;
      state.alertError = action.payload;
    },

    // ==========================================
    // Clear Actions
    // ==========================================
    clearBookingError: (state) => {
      state.bookingError = null;
    },
    clearBookingSuccess: (state) => {
      state.bookingSuccess = false;
    },
    clearCheckInError: (state) => {
      state.checkInError = null;
    },
    clearCheckInSuccess: (state) => {
      state.checkInSuccess = false;
    },
    clearAlertError: (state) => {
      state.alertError = null;
    },
    clearAlertSuccess: (state) => {
      state.alertSuccess = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetKidsClub: () => initialState,
  },
});

export const {
  fetchMyBookingsStart,
  fetchMyBookingsSuccess,
  fetchMyBookingsFailure,
  fetchUpcomingBookingsStart,
  fetchUpcomingBookingsSuccess,
  fetchUpcomingBookingsFailure,
  createBookingStart,
  createBookingSuccess,
  createBookingFailure,
  cancelBookingStart,
  cancelBookingSuccess,
  cancelBookingFailure,
  setSelectedBooking,
  fetchCapacityStart,
  fetchCapacitySuccess,
  fetchCapacityFailure,
  fetchCurrentCheckInsStart,
  fetchCurrentCheckInsSuccess,
  fetchCurrentCheckInsFailure,
  performCheckInStart,
  performCheckInSuccess,
  performCheckInFailure,
  performCheckOutStart,
  performCheckOutSuccess,
  performCheckOutFailure,
  updateParentLocationStart,
  updateParentLocationSuccess,
  updateParentLocationFailure,
  setSelectedCheckIn,
  fetchUnresolvedAlertsStart,
  fetchUnresolvedAlertsSuccess,
  fetchUnresolvedAlertsFailure,
  createAlertStart,
  createAlertSuccess,
  createAlertFailure,
  acknowledgeAlertStart,
  acknowledgeAlertSuccess,
  acknowledgeAlertFailure,
  resolveAlertStart,
  resolveAlertSuccess,
  resolveAlertFailure,
  clearBookingError,
  clearBookingSuccess,
  clearCheckInError,
  clearCheckInSuccess,
  clearAlertError,
  clearAlertSuccess,
  clearError,
  resetKidsClub,
} = kidsClubSlice.actions;

export default kidsClubSlice.reducer;
